import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { ReactNode } from 'react';

export type NewCareSeekerFormState = {
  firstName: string;
  lastName: string;
  email: string;
  sendInvitationEmail: boolean;
};

type NewCareSeekerFieldsProps = {
  value: NewCareSeekerFormState;
  errors: Partial<Record<keyof NewCareSeekerFormState, string>>;
  onChange: (value: NewCareSeekerFormState) => void;
};

export const NewCareSeekerFields = ({
  value,
  errors,
  onChange,
}: NewCareSeekerFieldsProps) => {
  const updateValue = <K extends keyof NewCareSeekerFormState>(
    key: K,
    nextValue: NewCareSeekerFormState[K],
  ) => onChange({ ...value, [key]: nextValue });

  return (
    <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldError message={errors.firstName}>
          <Label htmlFor="manual-referral-first-name">First name</Label>
          <Input
            id="manual-referral-first-name"
            value={value.firstName}
            onChange={(event) => updateValue('firstName', event.target.value)}
          />
        </FieldError>
        <FieldError message={errors.lastName}>
          <Label htmlFor="manual-referral-last-name">Last name</Label>
          <Input
            id="manual-referral-last-name"
            value={value.lastName}
            onChange={(event) => updateValue('lastName', event.target.value)}
          />
        </FieldError>
      </div>
      <FieldError message={errors.email}>
        <Label htmlFor="manual-referral-email">Email</Label>
        <Input
          id="manual-referral-email"
          type="email"
          value={value.email}
          onChange={(event) => updateValue('email', event.target.value)}
          placeholder="Optional unless invitation email is enabled"
        />
      </FieldError>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <Checkbox
          checked={value.sendInvitationEmail}
          onCheckedChange={(checked) => updateValue('sendInvitationEmail', checked === true)}
        />
        Send invitation email
      </label>
    </div>
  );
};

const FieldError = ({
  children,
  message,
}: {
  children: ReactNode;
  message?: string;
}) => (
  <div className="space-y-2">
    {children}
    {message && <p className="text-sm text-red-600">{message}</p>}
  </div>
);
